import { appConfig } from "../config";
import { ApiResponse } from "./types";
import { ApiClient } from "./apiClient";
import {BusinessActionDefinition} from "./BusinessActionDefinitionApi";
import {Employee} from "./EmployeeApi";

// ─── Types ───────────────────────────────────────────────────────────────────

export type BusinessActionStatus =
    | 'DRAFT'
    | 'PENDING_APPROVAL'
    | 'APPROVED'
    | 'REJECTED'
    | 'CANCELLED'
    | 'FULLY_APPLIED'
    | 'PARTIALLY_APPLIED';

export interface BusinessActionAttributeChange {
    id: string;
    attributeKey: string;
    previousValue: unknown;
    newValue: unknown;
    status: string;
}

// ── BusinessAction discriminated union ───────────────────────────────────────

/**
 * Fields present on every BusinessAction subtype.
 * Discriminated by the `entity` field, which mirrors the JPA
 * @DiscriminatorValue on each concrete class.
 */
interface BusinessActionBase {
    id: string;
    actionType: string;
    businessActionDefinition: BusinessActionDefinition;
    status: BusinessActionStatus;
    requestedAt: string;
    attributeChanges: BusinessActionAttributeChange[];
}

/**
 * A business action that creates a new employee.
 * employee is null until the action is applied and the Employee record is created.
 */
export interface EmployeeCreateAction extends BusinessActionBase {
    actionType: 'EmployeeCreate';
    employee: Employee | null;
}

/** A business action that updates an existing employee. */
export interface EmployeeUpdateAction extends BusinessActionBase {
    actionType: 'EmployeeUpdate';
    employee: Employee;
}

export type EmployeeRelatedBusinessAction = EmployeeCreateAction | EmployeeUpdateAction;

/**
 * Catch-all for business action subtypes that do not yet have a dedicated
 * interface. Adding a new typed subtype above automatically excludes it here.
 */
export interface GenericBusinessAction extends BusinessActionBase {
    actionType: string;
}

export type BusinessAction = EmployeeRelatedBusinessAction | GenericBusinessAction;

// ── Type guards ───────────────────────────────────────────────────────────────

export const isEmployeeCreateAction = (ba: BusinessAction): ba is EmployeeCreateAction =>
    ba.actionType === 'EmployeeCreate';

export const isEmployeeUpdateAction = (ba: BusinessAction): ba is EmployeeUpdateAction =>
    ba.actionType === 'EmployeeUpdate';

export const isEmployeeRelatedAction = (ba: BusinessAction): ba is EmployeeRelatedBusinessAction =>
    ba.actionType === 'EmployeeCreate' || ba.actionType === 'EmployeeUpdate';

// ── Proposal ─────────────────────────────────────────────────────────────────

/**
 * Mirrors {@code BusinessActionProposal} from the backend.
 *
 * {@code undoneAction} is populated only for undo actions, carrying the concrete
 * action instance that would be reversed. For normal actions it is absent.
 */
export interface BusinessActionProposal {
    definition: BusinessActionDefinition;
    undoneAction?: BusinessAction;
}

// ─── API ─────────────────────────────────────────────────────────────────────

const apiClient = new ApiClient(`${appConfig.backendUrl}/api/business-actions`);

/**
 * Fetches all currently permitted proposals for a given entity instance.
 * Each proposal pairs a definition with an optional undo target.
 *
 * @param entity    the entity type, e.g. "employee"
 * @param entityId  the entity identifier — for employees, the employeeCode
 */
export const fetchAllowedActions = (
    entity: string,
    entityId: string
): Promise<ApiResponse<BusinessActionProposal[]>> => {
    return apiClient.get<BusinessActionProposal[]>(
        `/${encodeURIComponent(entity)}/${encodeURIComponent(entityId)}`
    );
};